import { useEffect, useRef, useState } from 'react';
import { first } from 'rxjs/operators';
import { requestChartData } from '../../../../../../../stores/websocketStore/utils/outgoingMessages/charts/requestChartData';
import { groupByFunction } from '../../../../../../../stores/websocketStore/utils/rxjs/groupByFunction';
import style from './ChartContainer.module.css';
import { min, max } from 'd3-array';
import { scaleLinear } from 'd3-scale';
import { zoom, select, zoomTransform } from 'd3';
import { useWebsocketStore } from '../../../../../../../stores/websocketStore/websocketProvider';
import { MouseCoordinatesWrapper } from '../MouseCoordinatesWrapper';
import VerticalTicks from '../VerticalTicks';
import HorizontalTicks from '../HorizontalTicks';
import { OrdersContainer } from '../orders/OrdersContainer';
import ChartCrossHairs from '../ChartCrossHairs';
import SoftOrdersContainer from '../softOrders/SoftOrdersContainer';
import VerticalLabels from '../VerticalLabels';
import HorizontalLabels from '../HorizontalLabels';
import { CurrentCandle } from '../CurrentCandle';
import { OldCandles } from '../../OldCandles';
import { calculateMillisecondsBetweenCandles } from '../utils/calculateMillisecondsBetweenCandles';
import { useContextMenu } from 'react-contexify';
import { ChartContextMenu } from '../chartContextMenu/ChartContextMenu';
import { observer } from 'mobx-react-lite';
import { colors } from '../../../../../../../colorVars';
import { DisplayPreTactics } from '../displayPreTactics/DisplayPreTactics';

const bottomPadding = 15;
interface Props {
  autoSend?: boolean;
  tradePlanId: string;
  symbol: string;
  dataSource: string;
  brokerage: string;
  numberOfCandles: number;
  timeUnit: string;
  candleDuration: number;
  id: string | number;
  size: number;
  margin: number;
  showCandleNumbers: boolean;
  startHour: string | number;
  startMin: string | number;
}
export const RefactoredChartContainer: React.FC<Props> = observer(
  ({
    autoSend,
    tradePlanId,
    symbol,
    dataSource,
    brokerage,
    numberOfCandles,
    timeUnit,
    candleDuration,
    id,
    size,
    margin,
    showCandleNumbers,
    startHour,
    startMin,
  }: Props) => {
    const { chartSubject } = useWebsocketStore();
    const msgId =
      symbol + candleDuration + timeUnit + numberOfCandles + dataSource + id;
    const hash = `${symbol.toUpperCase()}-${dataSource}`;
    const { show } = useContextMenu({ id: tradePlanId });

    //STATE
    const [oldCandles, setOldCandles] = useState(null);
    const [currentCandle, setCurrentCandle] = useState(null);
    const [loading, setLoading] = useState(true);
    const [priceMinimum, setPriceMinimum] = useState(0);
    const [priceMaximum, setPriceMaximum] = useState(0);
    const [scaleX, setScaleX] = useState(null);
    const [scaleY, setScaleY] = useState(null);
    const [currentZoomState, setCurrentZoomState] = useState(null);
    const [currentYZoomState, setCurrentYZoomState] = useState(null);
    const [timeStampArray, setTimeStampArray] = useState(null);
    const [caliber, setCaliber] = useState(null);
    const [millisBetweenCandles, setMillisBetweenCandles] = useState(null);
    const [realNumberOfCandles, setRealNumberOfCandles] = useState(30);

    //REFS
    const zoomRef = useRef(null);
    const verticalZoomRef = useRef(null);
    const portalRef = useRef();

    //FUNCTIONS
    function updateOldCandles({ currentCandle }) {
      const oldCandlesCopy = [...oldCandles, currentCandle];
      oldCandlesCopy.shift();
      setPriceMinimum(min(oldCandlesCopy, (c) => c.low));
      setPriceMaximum(max(oldCandlesCopy, (c) => c.high));
      setOldCandles(oldCandlesCopy);
      const timeStampArrayCopy = [...timeStampArray, currentCandle.timeStamp];
      timeStampArrayCopy.shift();
      setTimeStampArray(timeStampArrayCopy);
    }
    const displayContextMenu = (e, y) => {
      // e.preventDefault();
      show(e, { props: { yCoordinate: y } });
    };

    // SETS CANDLE DATA, RUNS ON LOAD AND WHENEVER CHART PARAMETERS CHANGE. REQUESTS A NEW CHART AND SPLITS CHART DATA INTO OLD AND CURRENT CANDLES
    useEffect(() => {
      setLoading(true);
      setOldCandles(null);
      setCurrentCandle(null);
      setTimeStampArray(null);
      const chartObservable = groupByFunction(
        'msgId',
        msgId,
        chartSubject
      ).pipe(first());
      const chartSubscription = chartObservable.subscribe((msg) => {
        const candles = [...msg.candles];
        setRealNumberOfCandles(candles.length);
        setPriceMinimum(min(candles, (c) => c.low));
        setPriceMaximum(max(candles, (c) => c.high));
        setTimeStampArray(candles.map((candle) => candle.timeStamp));
        setCaliber(size / candles.length - margin);
        const lastCandle = candles.pop();
        setOldCandles(candles);
        setCurrentCandle(lastCandle);
        setLoading(false);
      });
      requestChartData({
        msgId,
        symbol,
        candleDuration,
        numberOfCandles,
        timeUnit,
        brokerage,
      });
      return () => chartSubscription.unsubscribe();
    }, [
      symbol,
      dataSource,
      brokerage,
      numberOfCandles,
      timeUnit,
      candleDuration,
      // size,
    ]);
    //SETS MILLISECONDS BETWEEN CANDLES
    useEffect(() => {
      const millis = calculateMillisecondsBetweenCandles({
        timeUnit,
        candleDuration,
      });
      setMillisBetweenCandles(millis);
    }, [timeUnit, candleDuration]);

    //SETS SCALES, RUNS AFTER CHART DATA IS RECEIVED, WHEN MIN/MAX CHANGES AND WHEN ZOOM CHANGES
    useEffect(() => {
      const xScale = scaleLinear()
        .domain([0, realNumberOfCandles])
        .range([0, size])
        .nice();
      if (currentZoomState !== null) {
        const newScale = currentZoomState.rescaleX(xScale);
        xScale.domain(newScale.domain());
        setScaleX(() => xScale);
        setCaliber(
          (size / realNumberOfCandles - margin / currentZoomState.k) *
            currentZoomState.k
        );
      } else {
        setScaleX(() => xScale);
        setCaliber(size / realNumberOfCandles - margin);
      }

      const yScale = scaleLinear()
        .domain([priceMinimum, priceMaximum])
        .range([size, 0])
        .nice();
      if (currentYZoomState !== null) {
        const newScale = currentYZoomState.rescaleY(yScale);
        yScale.domain(newScale.domain());
        setScaleY(() => yScale);
      } else setScaleY(() => yScale);
    }, [
      oldCandles,
      priceMinimum,
      priceMaximum,
      currentZoomState,
      currentYZoomState,
      size,
    ]);

    //SETS ZOOM STATE WHEN USER ZOOMS
    useEffect(() => {
      const svg = select(zoomRef.current);
      const zoomBehavior = zoom()
        .filter((event) => event.altKey)
        .scaleExtent([1, 10])
        .translateExtent([
          [0, 0],
          [size, size],
        ])
        .on('zoom', () => {
          setCurrentZoomState(zoomTransform(svg.node()));
        });
      zoomBehavior(svg);
    }, [size]);

    useEffect(() => {
      const svg = select(verticalZoomRef.current);
      const zoomBehavior = zoom()
        .filter((event) => event.altKey)
        .scaleExtent([0.1, 2])
        .translateExtent([
          [0, 0],
          [size, size],
        ])
        .on('zoom', () => {
          setCurrentYZoomState(zoomTransform(svg.node()));
        });
      zoomBehavior(svg);
    }, [size]);

    return (
      <div className={style.outerContainer}>
        <div
          style={{
            display: 'flex',
            width: 'fit-content',
          }}
        >
          <MouseCoordinatesWrapper
            tradePlanId={tradePlanId}
            displayContextMenu={displayContextMenu}
            svgRef={zoomRef}
          >
            <svg
              width={size}
              height={size + bottomPadding}
              ref={zoomRef}
              xmlns="http://www.w3.org/2000/svg"
              xmlnsXlink="http://www.w3.org/1999/xlink"
            >
              <line
                x1={0}
                x2={0}
                y1={0}
                y2={size + bottomPadding}
                stroke={colors.lightGrey}
              />
              <line x1={0} x2={size} y1={0} y2={0} stroke={colors.lightGrey} />

              {currentCandle ? (
                <CurrentCandle
                  currentCandle={currentCandle}
                  caliber={caliber}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  hash={hash}
                  loading={loading}
                  currentZoomState={currentZoomState}
                  portalRef={portalRef}
                  size={size}
                  secondToLast={oldCandles[oldCandles.length - 1]}
                  numberOfCandles={realNumberOfCandles}
                  updateOldCandles={updateOldCandles}
                  millisBetweenCandles={millisBetweenCandles}
                  priceMinimum={priceMinimum}
                  priceMaximum={priceMaximum}
                  setPriceMinimum={setPriceMinimum}
                  setPriceMaximum={setPriceMaximum}
                  dataSource={dataSource}
                  showCandleNumbers={showCandleNumbers}
                  startHour={startHour}
                  startMin={startMin}
                  timeUnit={timeUnit}
                  candleDuration={candleDuration}
                />
              ) : null}
              {oldCandles ? (
                <OldCandles
                  oldCandles={oldCandles}
                  caliber={caliber}
                  scaleX={scaleX}
                  scaleY={scaleY}
                  loading={loading}
                  currentZoomState={currentZoomState}
                  portalRef={portalRef}
                  size={size}
                  showCandleNumbers={showCandleNumbers}
                  timeUnit={timeUnit}
                  bottomPadding={bottomPadding}
                />
              ) : null}

              <VerticalTicks
                size={size}
                bottomPadding={bottomPadding}
                scaleY={scaleY}
                numberOfTicks={10}
                tickSize={5}
                weight={1}
              />
              <HorizontalTicks
                size={size}
                bottomPadding={bottomPadding}
                scaleX={scaleX}
                numberOfTicks={10}
                tickSize={5}
                weight={1}
              />
              {tradePlanId !== 'none' ? (
                <OrdersContainer
                  symbol={symbol}
                  tradePlanId={tradePlanId}
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              <ChartCrossHairs size={size} bottomPadding={bottomPadding} />
              {tradePlanId !== 'none' ? (
                <SoftOrdersContainer
                  tradePlanId={tradePlanId}
                  symbol={symbol}
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              {tradePlanId === 'newTradePlan' ? (
                <DisplayPreTactics
                  size={size}
                  scaleY={scaleY}
                  portalRef={portalRef}
                />
              ) : null}
              <g ref={portalRef}></g>
            </svg>
            <VerticalLabels
              verticalZoomRef={verticalZoomRef}
              scaleY={scaleY}
              size={size}
              fontSize="10px"
              numberOfTicks={10}
              bottomPadding={bottomPadding}
            />
            {tradePlanId !== 'none' ? (
              <ChartContextMenu
                hash={hash}
                timeUnit={timeUnit}
                candleDuration={candleDuration}
                autoSend={autoSend}
                tradePlanId={tradePlanId}
                scaleY={scaleY}
              />
            ) : null}
          </MouseCoordinatesWrapper>
        </div>
        <HorizontalLabels
          scaleX={scaleX}
          size={size}
          timeStampArray={timeStampArray}
          timeUnit={timeUnit}
        />
      </div>
    );
  }
);
